"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAvailability = void 0;
var _basketUpdated = require("@ori-events/basket-updated");
var _react = require("react");
const areAllPurchasesOutOfStock = items => {
  if (!items || items.length === 0) {
    return false;
  }
  return !items.some(({
    availableQuantity,
    purchasedQuantity
  }) => purchasedQuantity > 0 && (availableQuantity === undefined || Number(availableQuantity) > 0));
};

/**
 * Get a copy of selected items with the wasAvailabilityChange property re-set to false
 * for highlighting limited quantity from selected items.
 */
const resetAvailabilityChange = items => items.map(item => ({
  ...item,
  wasAvailabilityChange: false
}));

/**
 * Returns a new state with the selections adjusted according to available
 * quantities. But doesn't apply the state yet to be able to do checks on it first.
 */
const getValidSelectionState = (previousState, availableItems) => {
  if (availableItems.length === 0) {
    return previousState;
  }
  const result = [...previousState];
  availableItems.forEach(({
    itemCode,
    availableQuantity
  }) => {
    const indexes = result.reduce((acc, curr, i) => {
      if (curr.itemCode === itemCode) {
        acc.push(i);
      }
      return acc;
    }, []);
    indexes.forEach(index => {
      const item = result[index];
      const {
        selectedQuantity
      } = item;
      const itemReplacement = {
        ...item,
        availableQuantity,
        selectedQuantity: Math.min(Number(selectedQuantity), availableQuantity),
        wasAvailabilityChange: Number(selectedQuantity) > availableQuantity
      };
      result[index] = itemReplacement;
    });
  });
  return result;
};

/**
 * Works with availability state for concept offer hints
 * received after error response from failed apply offer action.
 */
const useAvailability = ({
  offerItems,
  offerType,
  requiredQuantity,
  setOfferItems
}) => {
  const isEnoughRemainingItems = (0, _react.useMemo)(() => (items, minimumQuantity) =>
  // some selected item exists in items, which is missing availability info,
  // i.e. is considered to be on stock as the availability for it wasn't checked yet
  items.some(({
    availableQuantity,
    selectedQuantity
  }) => selectedQuantity > 0 && availableQuantity === undefined) ||
  // or sum available quantities on selected items with availability,
  // the sum is bigger than requiredQuantity
  items.filter(({
    selectedQuantity
  }) => selectedQuantity > 0).map(({
    availableQuantity
  }) => availableQuantity ?? 0).reduce((acc, curr) => acc + curr, 0) > minimumQuantity, []);
  const isEnoughRemainingBenefits = (0, _react.useMemo)(() => items =>
  // item exists in items, which is missing availability info,
  // i.e. is considered to be on stock as the availability for it wasn't checked yet,
  // or some benefit has available quantity higher than 0
  items.some(({
    availableQuantity
  }) => availableQuantity === undefined || availableQuantity > 0), []);
  const isOfferApplicable = (0, _react.useCallback)(newAvailability => {
    if (newAvailability === undefined) {
      return true;
    }
    const isOperationNotAllowed = !newAvailability.length;
    if (isOperationNotAllowed) {
      return false;
    }
    const state = getValidSelectionState(offerItems, newAvailability);
    if (areAllPurchasesOutOfStock(state)) {
      return false;
    }
    if (offerType === _basketUpdated.BasketOfferTypes.CHOOSE_WITH_DISCOUNT) {
      const benefitsOrProductPurchases = state.filter(({
        isBenefit,
        purchasedQuantity
      }) => isBenefit || purchasedQuantity > 0);
      return isEnoughRemainingItems(benefitsOrProductPurchases, requiredQuantity);
    }
    const items = state.filter(({
      isBenefit
    }) => !isBenefit);
    const benefits = state.filter(({
      isBenefit
    }) => isBenefit);
    return isEnoughRemainingItems(items, requiredQuantity) && isEnoughRemainingBenefits(benefits);
  }, [isEnoughRemainingBenefits, isEnoughRemainingItems, offerItems, offerType, requiredQuantity]);
  const setAvailability = (0, _react.useCallback)(availability => {
    setOfferItems(items => getValidSelectionState(items, availability));
  }, [setOfferItems]);
  return {
    isOfferApplicable,
    resetAvailabilityChange,
    setAvailability
  };
};
exports.useAvailability = useAvailability;